import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import API from '../services/API';

const CheckCodeResult = () => {
    const navigate = useNavigate();
    const location = useLocation();

    // State management
    const [loading, setLoading] = useState(true);
    const [sellerInfo, setSellerInfo] = useState({
        id: null,
        name: null,
        phone: null,
        roleId: null,
        loading: false,
        error: null
    });

    // Get URL parameters
    const urlParams = new URLSearchParams(location.search);
    const productSerial = urlParams.get('productSerial');
    const scratchCode = urlParams.get('scratchCode');
    const sellerPhone = urlParams.get('sellerPhone');

    // Fetch seller information
    useEffect(() => {
        const fetchSellerInfo = async () => {
            if (!sellerPhone) {
                setLoading(false);
                return;
            }

            setSellerInfo(prev => ({ ...prev, loading: true, error: null }));

            try {
                const result = await API.checkUserExists(sellerPhone);
                console.log('Seller info result:', result);

                if (result.exists && result.userInfo) {
                    setSellerInfo({
                        id: result.userInfo.id || result.userInfo.userId,
                        name: result.userInfo.name || result.userInfo.fullName ||
                            (result.userInfo.firstName && result.userInfo.lastName ?
                                `${result.userInfo.firstName} ${result.userInfo.lastName}`.trim() : 'Đại lý'),
                        phone: sellerPhone,
                        roleId: result.userInfo.roleId,
                        loading: false,
                        error: null
                    });
                } else {
                    setSellerInfo({
                        id: null,
                        name: 'Đại lý',
                        phone: sellerPhone,
                        roleId: null,
                        loading: false,
                        error: 'Không tìm thấy thông tin đại lý'
                    });
                }
            } catch (error) {
                console.error('Error fetching seller info:', error);
                setSellerInfo({
                    id: null,
                    name: 'Đại lý',
                    phone: sellerPhone,
                    roleId: null,
                    loading: false,
                    error: 'Không thể tải thông tin đại lý'
                });
            } finally {
                setLoading(false);
            }
        };

        fetchSellerInfo();
    }, [sellerPhone]);

    const handleBackToCheck = () => {
        navigate('/');
    };

    if (loading) {
        return (
            <div className="gradient-bg min-h-screen flex items-center justify-center p-4">
                <div className="bg-white rounded-2xl shadow-lg p-8 text-center">
                    <div className="w-8 h-8 border-4 border-t-transparent rounded-full animate-spin mx-auto mb-4"
                        style={{ borderColor: '#277f4d', borderTopColor: 'transparent' }}></div>
                    <p className="text-gray-600">Đang tải dữ liệu...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="gradient-bg min-h-screen flex items-center justify-center p-4">
            <div className="w-full max-w-2xl">
                <div className="bg-white rounded-2xl shadow-lg p-6 md:p-8 fade-in" style={{ backgroundColor: '#E0F7E0' }}>
                    <div className="text-center">
                        {/* Logo */}
                        <div className="flex items-center justify-center mx-auto mb-6">
                            <img src="/images/logo-yodywhite.png" alt="YODY Logo" className="h-16" />
                        </div>

                        {/* Success Icon or Error Icon based on seller validity */}
                        {sellerInfo.roleId === 14 ? (
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="80"
                                height="80"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                className="mx-auto text-green-500 mb-4"
                            >
                                <path d="M12 22c5.523 0 10-4.477 10-10S17.523 2 12 2 2 6.477 2 12s4.477 10 10 10z"></path>
                                <path d="m9 12 2 2 4-4"></path>
                            </svg>
                        ) : (
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="80"
                                height="80"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                className="mx-auto text-red-500 mb-4"
                            >
                                <circle cx="12" cy="12" r="10"></circle>
                                <line x1="15" y1="9" x2="9" y2="15"></line>
                                <line x1="9" y1="9" x2="15" y2="15"></line>
                            </svg>
                        )}

                        {/* Thank you message or error message based on seller validity */}
                        <h1 className="text-xl md:text-2xl font-bold text-gray-800 mb-4">
                            {sellerInfo.roleId === 14
                                ? "Xin cảm ơn Quý khách đã tin dùng sản phẩm của chúng tôi."
                                : "Thông tin người bán không tồn tại trong hệ thống đại lý phân phối."}
                        </h1>

                        {/* Product verification info */}
                        <div className="bg-white rounded-lg p-4 md:p-6 mb-6 text-left">
                            {sellerInfo.loading ? (
                                <div className="flex items-center justify-center py-4">
                                    <div className="w-6 h-6 border-4 border-t-transparent rounded-full animate-spin"
                                        style={{ borderColor: '#277f4d', borderTopColor: 'transparent' }}></div>
                                    <span className="ml-3 text-gray-600">Đang tải thông tin đại lý...</span>
                                </div>
                            ) : sellerInfo.roleId === 14 ? (
                                <>
                                    <p className="text-gray-800 mb-4 leading-relaxed">
                                        Sản phẩm được chứng nhận là hàng chính hãng, được phân phối bởi Đại lý chính thức:
                                    </p>
                                    <div className="flex items-center py-2">
                                        <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            width="24"
                                            height="24"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            stroke="currentColor"
                                            strokeWidth="2"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            className="text-green-500 mr-3 flex-shrink-0"
                                        >
                                            <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"></path>
                                            <circle cx="9" cy="7" r="4"></circle>
                                            <path d="M22 21v-2a4 4 0 0 0-3-3.87"></path>
                                            <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
                                        </svg>
                                        <div>
                                            <p className="font-bold text-gray-800" style={{ color: '#277f4d' }}>
                                                {sellerInfo.id ? `${sellerInfo.id} - ${sellerInfo.name}` : sellerInfo.name}
                                            </p>
                                            <p className="text-sm text-gray-600 mt-1">SĐT: {sellerInfo.phone}</p>
                                        </div>
                                    </div>
                                </>
                            ) : (
                                <div className="flex items-start py-2">
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        width="24"
                                        height="24"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        stroke="currentColor"
                                        strokeWidth="2"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        className="text-red-500 mr-3 flex-shrink-0 mt-1"
                                    >
                                        <circle cx="12" cy="12" r="10"></circle>
                                        <line x1="12" y1="8" x2="12" y2="12"></line>
                                        <line x1="12" y1="16" x2="12.01" y2="16"></line>
                                    </svg>
                                    <div>
                                        <p className="text-gray-800 leading-relaxed">
                                            Vui lòng kiểm tra lại SĐT người bán hoặc yêu cầu người bán cung cấp SĐT đã đăng ký với công ty.
                                        </p>
                                        {sellerInfo.phone && (
                                            <p className="text-sm text-gray-600 mt-2">SĐT đã nhập: {sellerInfo.phone}</p>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Product details (optional - can be hidden or shown) */}
                        <div className="bg-white rounded-lg p-4 mb-6 text-left text-sm text-gray-600">
                            <p className="mb-2"><span className="font-semibold">Số serial tem:</span> {productSerial}</p>
                            <p><span className="font-semibold">Mã dưới lớp phủ cào:</span> {scratchCode}</p>
                        </div>

                        {/* Back button */}
                        <button
                            onClick={handleBackToCheck}
                            className="w-full text-white font-semibold py-3 px-4 rounded-lg transition-transform hover:scale-105"
                            style={{ backgroundColor: '#277f4d' }}
                            onMouseEnter={(e) => e.target.style.backgroundColor = '#1f5f3a'}
                            onMouseLeave={(e) => e.target.style.backgroundColor = '#277f4d'}
                        >
                            Kiểm tra sản phẩm khác
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CheckCodeResult;
